/**
 * 圈选（框选）Hook — 世界坐标，支持跨页与工作区元素
 */

import { useState, useRef, useCallback, useEffect, RefObject } from 'react';
import { screenToWorld } from '@/components/canvas/document/coordinates';
import type { Element } from '@/components/canvas/document/publicTypes';
import {
  type MarqueePageSlice,
  type MarqueeRect,
  resolveMarqueeSelection,
} from './marqueeSelectUtils';

export type { MarqueeRect, MarqueeScope } from './marqueeSelectUtils';

/** 激活页板面内空白（非 chrome / 元素 / 手柄 / 页框拖条） */
export function isActivePageBlankTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (!target.closest('[data-page]')) return false;
  if (target.closest('[data-page-chrome]')) return false;
  if (target.closest('[data-page-frame-drag]')) return false;
  if (target.closest('[data-handle]')) return false;
  if (target.closest('[data-element-hit]')) return false;
  if (target.closest('[data-block-hit]')) return false;
  return true;
}

interface UseMarqueeSelectOptions {
  workspaceRef: RefObject<HTMLDivElement | null>;
  zoom: number;
  panX: number;
  panY: number;
  pages: MarqueePageSlice[];
  workspaceElements: Element[];
  currentPageId: string;
  selectMarqueeResult: (payload: {
    elementIds: string[];
    pageIds: string[];
    scope: import('./marqueeSelectUtils').MarqueeScope;
  }) => void;
  clearSelection: () => void;
  /** 页内空白点击（小位移抬起）时选中页框，避免被 clearSelection 清掉 */
  onPageBlankClick?: () => void;
  isSpacePressed: boolean;
  penToolActive: boolean;
  freehandToolActive: boolean;
  eraserToolActive: boolean;
  tableDrawMode?: boolean;
  tableDrawPending?: boolean;
  editingGroupId?: string | null;
}

export function useMarqueeSelect({
  workspaceRef,
  zoom,
  panX,
  panY,
  pages,
  workspaceElements,
  currentPageId,
  selectMarqueeResult,
  clearSelection,
  onPageBlankClick,
  isSpacePressed,
  penToolActive,
  freehandToolActive,
  eraserToolActive,
  tableDrawMode = false,
  tableDrawPending = false,
  editingGroupId,
}: UseMarqueeSelectOptions) {
  const [marquee, setMarquee] = useState<MarqueeRect | null>(null);

  const startRef = useRef<{ x: number; y: number } | null>(null);

  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const panRef = useRef({ panX, panY });
  panRef.current = { panX, panY };
  const pagesRef = useRef(pages);
  pagesRef.current = pages;
  const workspaceElementsRef = useRef(workspaceElements);
  workspaceElementsRef.current = workspaceElements;
  const currentPageIdRef = useRef(currentPageId);
  currentPageIdRef.current = currentPageId;
  const selectMarqueeResultRef = useRef(selectMarqueeResult);
  selectMarqueeResultRef.current = selectMarqueeResult;
  const clearSelectionRef = useRef(clearSelection);
  clearSelectionRef.current = clearSelection;
  const onPageBlankClickRef = useRef(onPageBlankClick);
  onPageBlankClickRef.current = onPageBlankClick;
  const marqueeTargetRef = useRef<HTMLElement | null>(null);
  const editingGroupIdRef = useRef(editingGroupId);
  editingGroupIdRef.current = editingGroupId;

  const prevMatchedKeyRef = useRef('');

  const toWorld = useCallback((clientX: number, clientY: number) => {
    const rect = workspaceRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    const { panX: px, panY: py } = panRef.current;
    return screenToWorld(clientX, clientY, rect, px, py, zoomRef.current);
  }, [workspaceRef]);

  const applyMarquee = useCallback(
    (start: { x: number; y: number }, end: { x: number; y: number }, mr: { x: number; y: number; w: number; h: number }) => {
      const result = resolveMarqueeSelection(
        pagesRef.current,
        workspaceElementsRef.current,
        currentPageIdRef.current,
        editingGroupIdRef.current ?? null,
        start,
        end,
        mr,
      );
      const key = `${result.scope}|${[...result.pageIds].sort().join(',')}|${[...result.elementIds].sort().join(',')}`;
      if (key !== prevMatchedKeyRef.current) {
        prevMatchedKeyRef.current = key;
        if (result.elementIds.length === 0 && result.pageIds.length === 0) {
          // 页内空白点选：圈选过程中勿 clear，否则页框选区在 mouseup 前被清掉
          if (!isActivePageBlankTarget(marqueeTargetRef.current)) {
            clearSelectionRef.current();
          }
        } else {
          selectMarqueeResultRef.current(result);
        }
      }
    },
    [],
  );

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!startRef.current) return;
      const pos = toWorld(e.clientX, e.clientY);
      const { x: sx, y: sy } = startRef.current;
      const mr = {
        x: Math.min(sx, pos.x),
        y: Math.min(sy, pos.y),
        w: Math.abs(pos.x - sx),
        h: Math.abs(pos.y - sy),
      };
      setMarquee({ x: mr.x, y: mr.y, width: mr.w, height: mr.h });
      applyMarquee(startRef.current, pos, mr);
    };

    const onMouseUp = (e: MouseEvent) => {
      if (!startRef.current) return;

      const pos = toWorld(e.clientX, e.clientY);
      const start = startRef.current;
      const mr = {
        x: Math.min(start.x, pos.x),
        y: Math.min(start.y, pos.y),
        w: Math.abs(pos.x - start.x),
        h: Math.abs(pos.y - start.y),
      };

      startRef.current = null;
      prevMatchedKeyRef.current = '';
      setMarquee(null);
      const downTarget = marqueeTargetRef.current;
      marqueeTargetRef.current = null;

      if (mr.w <= 4 && mr.h <= 4) {
        if (isActivePageBlankTarget(downTarget)) {
          onPageBlankClickRef.current?.();
        } else {
          clearSelectionRef.current();
        }
        return;
      }

      const result = resolveMarqueeSelection(
        pagesRef.current,
        workspaceElementsRef.current,
        currentPageIdRef.current,
        editingGroupIdRef.current ?? null,
        start,
        pos,
        mr,
      );

      if (result.elementIds.length > 0 || result.pageIds.length > 0) {
        selectMarqueeResultRef.current(result);
        const blockClick = (ev: MouseEvent) => {
          ev.stopPropagation();
          window.removeEventListener('click', blockClick, true);
        };
        window.addEventListener('click', blockClick, true);
      } else {
        clearSelectionRef.current();
      }
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [toWorld, applyMarquee]);

  const shouldStartMarquee = useCallback((target: HTMLElement) => {
    if (target.closest('[data-handle]')) return false;
    if (target.closest('[data-block-hit]')) return false;
    const hit = target.closest('[data-element-hit]') as HTMLElement | null;
    if (hit) {
      const id = hit.getAttribute('data-element-id');
      const page = pagesRef.current.find((p) => p.elements.some((x) => x.id === id));
      const el = id && page ? page.elements.find((x) => x.id === id) : undefined;
      if (!el || !el.interaction.locked) return false;
    }
    return true;
  }, []);

  const beginMarquee = useCallback(
    (clientX: number, clientY: number, target: HTMLElement) => {
      marqueeTargetRef.current = target;
      const pos = toWorld(clientX, clientY);
      startRef.current = pos;
      prevMatchedKeyRef.current = '';
      setMarquee({ x: pos.x, y: pos.y, width: 0, height: 0 });
    },
    [toWorld],
  );

  const handleCanvasMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;
      if (
        isSpacePressed ||
        penToolActive ||
        freehandToolActive ||
        eraserToolActive ||
        tableDrawMode ||
        tableDrawPending
      ) {
        return;
      }
      const target = e.target as HTMLElement;
      if (!shouldStartMarquee(target)) return;
      beginMarquee(e.clientX, e.clientY, target);
    },
    [
      isSpacePressed,
      penToolActive,
      freehandToolActive,
      eraserToolActive,
      tableDrawMode,
      tableDrawPending,
      shouldStartMarquee,
      beginMarquee,
    ],
  );

  const handleStageMarqueeMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;
      if (
        isSpacePressed ||
        penToolActive ||
        freehandToolActive ||
        eraserToolActive ||
        tableDrawMode ||
        tableDrawPending
      ) {
        return;
      }
      const target = e.target as HTMLElement;
      if (target.closest('[data-page]') || target.closest('[data-page-chrome]')) return;
      if (!shouldStartMarquee(target)) return;
      beginMarquee(e.clientX, e.clientY, target);
    },
    [
      isSpacePressed,
      penToolActive,
      freehandToolActive,
      eraserToolActive,
      tableDrawMode,
      tableDrawPending,
      shouldStartMarquee,
      beginMarquee,
    ],
  );

  return {
    marquee,
    handleCanvasMouseDown,
    handleStageMarqueeMouseDown,
    isMarqueeActive: marquee !== null,
  };
}
